import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * BottomGlassSheet — a reusable liquid-glass draggable sheet.
 *
 * Design system component: this same sheet is used across Cycle,
 * and will be reused in other tabs (Care, Insights) later.
 *
 * Features:
 *   • Native iOS spring animation (cubic-bezier 0.32 .72 0 1, 780ms)
 *   • Real drag with rubber-band clamp at bounds
 *   • Velocity-based fling detection (0.48 px/ms threshold)
 *   • Three snap states: collapsed peek · expanded
 *   • Backdrop scrim fades in on expand
 *   • Phase-tinted inner glass (very subtle 3-5% via CSS)
 *
 * Props:
 *   peekHeight      px visible when collapsed (default 148 to show one row)
 *   collapsedOffset px visible for handle+peek only (default 62)
 *   defaultOpen     boolean — start expanded
 *   header          { title, sub }
 *   children        content shown when expanded
 *   onStateChange   (isExpanded) => void   optional
 */
export default function BottomGlassSheet({
  header,
  collapsedOffset = 42,
  expandedTopVh   = 12,   // gap from top of viewport when expanded
  flingThreshold  = 0.48,
  defaultOpen     = false,
  onStateChange,
  children,
}) {
  const sheetRef = useRef(null);
  const [expanded, setExpanded] = useState(defaultOpen);
  const [dragging, setDragging] = useState(false);

  // Drag state (refs to avoid re-renders during move)
  const drag = useRef({
    active: false, startY: 0, startTranslate: 0, currentY: 0,
    lastY: 0, lastT: 0, velocity: 0, downY: 0,
  });

  const heights = useCallback(() => {
    const sheet = sheetRef.current;
    if (!sheet) return { collapsedY: 0, expandedY: 0 };
    return {
      collapsedY: sheet.offsetHeight - collapsedOffset,
      expandedY:  window.innerHeight * (expandedTopVh / 100),
    };
  }, [collapsedOffset, expandedTopVh]);

  const getTranslateY = useCallback(() => {
    const sheet = sheetRef.current; if (!sheet) return 0;
    const m = getComputedStyle(sheet).transform;
    if (m === 'none') return 0;
    const parts = m.match(/matrix.*\((.+)\)/)?.[1]?.split(', ') || [];
    return parseFloat(parts[5] ?? parts[13] ?? 0);
  }, []);

  const rubberband = (y, min, max) => {
    if (y < min) return min - Math.pow(min - y, 0.62);
    if (y > max) return max + Math.pow(y - max, 0.62);
    return y;
  };

  useEffect(() => {
    onStateChange?.(expanded);
  }, [expanded, onStateChange]);

  const onPointerMove = useCallback((e) => {
    if (!drag.current.active) return;
    if (e.cancelable) e.preventDefault();
    const y = e.touches ? e.touches[0].clientY : e.clientY;
    const now = performance.now();
    const dt = now - drag.current.lastT;
    if (dt > 0) drag.current.velocity = (y - drag.current.lastY) / dt;
    drag.current.lastY = y; drag.current.lastT = now;
    const dy = y - drag.current.startY;
    const { collapsedY, expandedY } = heights();
    drag.current.currentY = rubberband(drag.current.startTranslate + dy, expandedY, collapsedY);
    if (sheetRef.current) sheetRef.current.style.transform = `translateX(-50%) translateY(${drag.current.currentY}px)`;
  }, [heights]);

  const onPointerUp = useCallback(() => {
    if (!drag.current.active) return;
    drag.current.active = false;
    setDragging(false);
    if (sheetRef.current) sheetRef.current.style.transform = '';
    // If the pointer never actually moved, treat this as a pure click
    // and let the peek's onClick handler own the toggle. Prevents the
    // "click while expanded doesn't collapse" issue reported by QA.
    const noMovement = Math.abs(drag.current.currentY - drag.current.startTranslate) < 2
                       && Math.abs(drag.current.velocity) < 0.05;
    if (noMovement) {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup',   onPointerUp);
      window.removeEventListener('touchmove',   onPointerMove);
      window.removeEventListener('touchend',    onPointerUp);
      return;
    }
    const { collapsedY, expandedY } = heights();
    let nextExpanded;
    if (drag.current.velocity < -flingThreshold)      nextExpanded = true;
    else if (drag.current.velocity > flingThreshold)  nextExpanded = false;
    else {
      const midpoint = (collapsedY + expandedY) / 2;
      nextExpanded = drag.current.currentY < midpoint;
    }
    setExpanded(nextExpanded);
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup',   onPointerUp);
    window.removeEventListener('touchmove',   onPointerMove);
    window.removeEventListener('touchend',    onPointerUp);
  }, [flingThreshold, heights, onPointerMove]);

  const onPointerDown = useCallback((e) => {
    drag.current.active = true;
    drag.current.startY = e.touches ? e.touches[0].clientY : e.clientY;
    drag.current.downY = drag.current.startY;
    drag.current.startTranslate = getTranslateY();
    drag.current.currentY = drag.current.startTranslate;
    drag.current.lastY = drag.current.startY;
    drag.current.lastT = performance.now();
    drag.current.velocity = 0;
    setDragging(true);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup',   onPointerUp);
    window.addEventListener('touchmove',   onPointerMove, { passive: false });
    window.addEventListener('touchend',    onPointerUp);
  }, [getTranslateY, onPointerMove, onPointerUp]);

  const onPeekTap = (e) => {
    // If pointer barely moved between down and up, treat as a tap.
    if (Math.abs(e.clientY - drag.current.downY) < 6 && !drag.current.active) {
      setExpanded(v => !v);
    }
  };

  const sheetClass =
    'cx-sheet' +
    (expanded ? ' cx-expanded' : '') +
    (dragging ? ' cx-dragging' : '');

  return (
    <>
      <div
        className={'cx-sheet-scrim' + (expanded ? ' cx-visible' : '')}
        onClick={() => setExpanded(false)}
        aria-hidden="true"
      />
      <aside ref={sheetRef} className={sheetClass} data-testid="cycle-sheet">
        <div
          className="cx-sheet-handle-area"
          onPointerDown={onPointerDown}
          data-testid="cycle-sheet-handle"
        >
          <div className="cx-sheet-handle"/>
        </div>
        <div
          className="cx-sheet-peek"
          onPointerDown={onPointerDown}
          onClick={() => setExpanded((e) => !e)}
          data-testid="cycle-sheet-peek"
        >
          <div>
            <div className="cx-peek-title">{header?.title}</div>
            <div className="cx-peek-sub">{header?.sub}</div>
          </div>
          <div className="cx-peek-chev">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
              <path d="m6 15 6-6 6 6"/>
            </svg>
          </div>
        </div>
        <div className="cx-sheet-body">{children}</div>
      </aside>
    </>
  );
}
